/** @file resizeEntry: an entry's new size and pivot pixel for a dragged edge or corner (pivot pixel kept). */

/** A tile size and its pivot pixel (`pivotFrac x size`, image px from the top-left). */
export type SizePivot = { size: [number, number]; pivot: [number, number] };

/** One axis: `side` -1 pulls the min edge (left / top), 1 the max edge, 0 leaves the axis; `delta` in image px. */
function axis(size: number, pivot: number, side: number, delta: number): [number, number] {
  if (side === 0) return [size, pivot];
  let grow = Math.round(side * delta);
  let low = 1 - size;
  // a pivot inside the tile stays inside: the max edge stops at the pivot pixel, the min edge too
  if (pivot >= 0 && pivot <= size) low = Math.max(low, side > 0 ? Math.ceil(pivot - size) : Math.ceil(-pivot));
  grow = Math.max(grow, low);
  return [size + grow, side < 0 ? pivot + grow : pivot];
}

/**
 * The entry after dragging the edges `sides` (per image axis, see `axis`) by `delta` image px: the size changes in
 * whole pixels (at least 1) and the pivot stays on the same image pixel, so pulling the min edge (the pivot's side
 * when the pivot sits there) moves the pivot pixel by the rows or columns added in front of it; the max edge leaves
 * it. A pivot inside the tile never leaves it.
 */
export function resizeEntry(start: SizePivot, sides: readonly [number, number], delta: readonly [number, number]): SizePivot {
  const [w, px] = axis(start.size[0], start.pivot[0], sides[0], delta[0]);
  const [h, py] = axis(start.size[1], start.pivot[1], sides[1], delta[1]);
  return { size: [w, h], pivot: [px, py] };
}
